// ===== 1. subjectsSlice.js =====
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/subjects/`;

export const fetchSubjects = createAsyncThunk('subjects/fetchSubjects', async () => {
  const res = await axios.get(BASE_URL);
  return res.data;
});

export const fetchClassSubjects = createAsyncThunk('subjects/fetchClassSubjects', async (classId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/subjects/`);
  return res.data;
});

export const addSubject = createAsyncThunk('subjects/addSubject', async (subject) => {
  const res = await axios.post(BASE_URL, subject);
  return res.data;
});

export const updateSubject = createAsyncThunk('subjects/updateSubject', async ({ id, ...subjectData }) => {
  const res = await axios.patch(`${BASE_URL}${id}/`, subjectData);
  return res.data;
});

export const deleteSubject = createAsyncThunk('subjects/deleteSubject', async (id) => {
  await axios.delete(`${BASE_URL}${id}/`);
  return id;
});

export const archiveSubject = createAsyncThunk('subjects/archiveSubject', async (id) => {
  const res = await axios.patch(`${BASE_URL}${id}/archive/`);
  return { id, ...res.data };
});

export const unarchiveSubject = createAsyncThunk('subjects/unarchiveSubject', async (id) => {
  const res = await axios.patch(`${BASE_URL}${id}/unarchive/`);
  return { id, ...res.data };
});

export const fetchSubjectsSummary = createAsyncThunk('subjects/fetchSummary', async () => {
  const res = await axios.get(`${BASE_URL}summary/`);
  return res.data;
});

const subjectsSlice = createSlice({
  name: 'subjects',
  initialState: {
    subjects: [],
    classSubjects: [],
    summary: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch all subjects
      .addCase(fetchSubjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubjects.fulfilled, (state, action) => {
        state.subjects = action.payload;
        state.loading = false;
      })
      .addCase(fetchSubjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch class subjects
      .addCase(fetchClassSubjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClassSubjects.fulfilled, (state, action) => {
        state.classSubjects = action.payload;
        state.loading = false;
      })
      .addCase(fetchClassSubjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Add subject
      .addCase(addSubject.fulfilled, (state, action) => {
        state.subjects.push(action.payload);
        state.classSubjects.push(action.payload);
      })
      
      // Update subject
      .addCase(updateSubject.fulfilled, (state, action) => {
        const index = state.subjects.findIndex(s => s.id === action.payload.id);
        if (index !== -1) state.subjects[index] = action.payload;
        
        const classIndex = state.classSubjects.findIndex(s => s.id === action.payload.id);
        if (classIndex !== -1) state.classSubjects[classIndex] = action.payload;
      })
      
      // Delete subject
      .addCase(deleteSubject.fulfilled, (state, action) => {
        state.subjects = state.subjects.filter(s => s.id !== action.payload);
        state.classSubjects = state.classSubjects.filter(s => s.id !== action.payload);
      })
      
      // Archive subject
      .addCase(archiveSubject.fulfilled, (state, action) => {
        const updateSubjectInArray = (array) => {
          const index = array.findIndex(s => s.id === action.payload.id);
          if (index !== -1) {
            array[index] = { ...array[index], is_archived: true };
          }
        };
        updateSubjectInArray(state.subjects);
        updateSubjectInArray(state.classSubjects);
      })
      
      // Unarchive subject
      .addCase(unarchiveSubject.fulfilled, (state, action) => {
        const updateSubjectInArray = (array) => {
          const index = array.findIndex(s => s.id === action.payload.id);
          if (index !== -1) {
            array[index] = { ...array[index], is_archived: false };
          }
        };
        updateSubjectInArray(state.subjects);
        updateSubjectInArray(state.classSubjects);
      })
      
      // Fetch summary
      .addCase(fetchSubjectsSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      });
  }
});

export const { clearError } = subjectsSlice.actions;
export default subjectsSlice.reducer;